// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React from 'react';

export function EberhartView({ data }) {
  if (!data) return null;
  const params = data.stability_parameters || {};
  const anova = data.stability_anova || {};
  return (
    <div className="space-y-6" data-testid="eberhart-view">
      {data.interpretation && (
        <div className="bg-[#FEF3E2] rounded-2xl p-5 border border-[#F39C12]/30">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Canonical bi-Regression Plot — one line per genotype */}
      {data.bi_regression_chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="eberhart-bi-regression">
          <div className="bg-[#F39C12]/10 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#B8770D] uppercase tracking-wider">bi-Regression Plot — One Line per Genotype</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">Eberhart &amp; Russell 1966 · Slope = bᵢ · Dotted gray = b = 1 reference</p>
          </div>
          <img src={`data:image/png;base64,${data.bi_regression_chart}`} alt="bi-Regression" className="w-full" />
        </div>
      )}

      {/* Stability ANOVA partition */}
      {Object.keys(anova).length > 0 && (
        <div className="overflow-x-auto" data-testid="eberhart-anova">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Stability ANOVA Partition (Eberhart-Russell 1966)</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#F39C12] text-white">
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-center">SS</th>
              <th className="px-3 py-2 text-center">DF</th>
              <th className="px-3 py-2 text-center">MS</th>
              <th className="px-3 py-2 text-center">F</th>
              <th className="px-3 py-2 text-center">P</th>
              <th className="px-3 py-2 text-center">Sig</th>
            </tr></thead>
            <tbody>
              {Object.entries(anova).map(([k, v], i) => (
                <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FEF6E7]'}>
                  <td className="px-3 py-1.5 font-medium">{k}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v.SS}</td>
                  <td className="px-3 py-1.5 text-center">{v.DF}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v.MS ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v.F ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v.P ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`font-bold ${v.Sig === '**' ? 'text-green-700' : v.Sig === '*' ? 'text-amber-700' : 'text-gray-500'}`}>{v.Sig}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stability parameters with significance tests */}
      {Object.keys(params).length > 0 && (
        <div className="overflow-x-auto" data-testid="eberhart-params">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Stability Parameters with Significance Tests</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#F39C12] text-white">
              <th className="px-3 py-2 text-left">Genotype</th>
              <th className="px-3 py-2 text-center">Mean</th>
              <th className="px-3 py-2 text-center">bᵢ</th>
              <th className="px-3 py-2 text-center">SE(bᵢ)</th>
              <th className="px-3 py-2 text-center">t (bᵢ-1)</th>
              <th className="px-3 py-2 text-center">P (bᵢ vs 1)</th>
              <th className="px-3 py-2 text-center">S²dᵢ</th>
              <th className="px-3 py-2 text-center">F (S²dᵢ vs 0)</th>
              <th className="px-3 py-2 text-center">P (S²dᵢ)</th>
              <th className="px-3 py-2 text-center">Verdict</th>
            </tr></thead>
            <tbody>
              {Object.entries(params).map(([g, p], i) => (
                <tr key={g} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FEF6E7]'}>
                  <td className="px-3 py-1.5 font-medium">{g}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.mean}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.bi} <span className="text-[9px] text-gray-500">{p.bi_sig}</span></td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.SE_bi ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.t_bi_minus_1 ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.p_bi ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.s2di} <span className="text-[9px] text-gray-500">{p.s2di_sig}</span></td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.F_s2di ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.p_s2di ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${p.interpretation === 'Stable' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {p.interpretation}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#6E6B66] mt-2 italic">
            Significance markers: <strong>**</strong> p&lt;0.01, <strong>*</strong> p&lt;0.05, <strong>ns</strong> not significant.
            bᵢ ≈ 1 with non-significant S²dᵢ → genotype is stable; bᵢ &gt; 1 → above-average response (favourable for good envs);
            bᵢ &lt; 1 → below-average response (favourable for poor envs).
          </p>
        </div>
      )}

      {/* Environmental Index */}
      {data.env_index && (
        <div className="overflow-x-auto" data-testid="eberhart-env-index">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Environmental Index Iⱼ = (ē·ⱼ - ē··)</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#F39C12] text-white">
              <th className="px-3 py-2 text-left">Environment</th>
              <th className="px-3 py-2 text-center">Iⱼ</th>
              <th className="px-3 py-2 text-center">Quality</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.env_index).map(([e, v], i) => (
                <tr key={e} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FEF6E7]'}>
                  <td className="px-3 py-1.5 font-medium">{e}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v}</td>
                  <td className="px-3 py-1.5 text-center text-[11px]">
                    {v > 0 ? <span className="text-green-700">Favourable (+)</span> : v < 0 ? <span className="text-red-700">Unfavourable (-)</span> : <span className="text-gray-500">Neutral (0)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#6E6B66] mt-2">
            Grand mean = <span className="font-mono">{data.grand_mean}</span> · ΣIⱼ² = <span className="font-mono">{data.sum_I_squared}</span> · Reps/cell = <span className="font-mono">{data.n_reps}</span>
          </p>
        </div>
      )}

      {/* Legacy 2-panel chart */}
      {data.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden">
          <div className="bg-[#F39C12]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#B8770D] uppercase tracking-wider">Summary: Mean × bᵢ scatter + S²dᵢ bars</h4>
          </div>
          <img src={`data:image/png;base64,${data.chart}`} alt="Stability summary" className="w-full" />
        </div>
      )}
    </div>
  );
}

/* ============================================================
   GGE BIPLOT VIEW (Sub-Batch 3S — 4 separate views)
============================================================ */


export function GGEBiplotView({ data }) {
  if (!data) return null;
  const adequacy = data.adequacy_flag || 'UNKNOWN';
  const adequacyColor = data.adequacy_color || '#7F8C8D';
  return (
    <div className="space-y-6" data-testid="gge-view">
      {data.interpretation && (
        <div className="bg-[#EAF4FB] rounded-2xl p-5 border border-[#4F46E5]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Variance / Adequacy flag panel */}
      {data.variance_table && data.variance_table.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="gge-variance">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E6E1]" style={{ background: `${adequacyColor}15` }}>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider" style={{ color: adequacyColor }}>GGE Variance Adequacy: {adequacy}</h4>
              <p className="text-[11px] text-[#6E6B66] mt-0.5">{data.adequacy_note}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold font-mono" style={{ color: adequacyColor }}>
                {data.variance_table[1]?.cumulative_pct ?? data.variance_table[0].cumulative_pct}%
              </div>
              <div className="text-[10px] text-[#8A8580] uppercase">PC1+PC2</div>
            </div>
          </div>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-gray-50">
              <th className="px-3 py-2 text-left">Axis</th>
              <th className="px-3 py-2 text-center">Singular Value</th>
              <th className="px-3 py-2 text-center">Eigenvalue (λ²)</th>
              <th className="px-3 py-2 text-center">% Variance</th>
              <th className="px-3 py-2 text-center">Cumulative %</th>
            </tr></thead>
            <tbody>
              {data.variance_table.map((row, i) => (
                <tr key={row.axis} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1.5 font-medium">{row.axis}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{row.singular_value}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{row.eigenvalue}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{row.pct_variance}%</td>
                  <td className="px-3 py-1.5 text-center font-mono font-bold">{row.cumulative_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PC decomposition honesty note */}
      {data.pc_decomposition_note && (
        <div className="bg-[#EFEEFB] border-l-4 border-[#4F46E5] px-4 py-3 rounded-md" data-testid="gge-pc-note">
          <p className="text-[12px] text-[#2C2A28] italic leading-relaxed">{data.pc_decomposition_note}</p>
        </div>
      )}

      {/* Four separate full-size biplots — per user spec, NOT composited */}
      {data.which_won_where_plot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="gge-which-won-where">
          <div className="bg-[#4F46E5]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#4F46E5] uppercase tracking-wider">GGE Biplot 1 · Which-Won-Where (Polygon View)</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">Vertex genotypes are "winners" of one or more mega-environments. Environments in the same sector are won by the same vertex.</p>
          </div>
          <img src={`data:image/png;base64,${data.which_won_where_plot}`} alt="GGE Which-Won-Where" className="w-full" />
        </div>
      )}

      {/* Sub-Batch 3S-Tail — Which-Won-Where summary table */}
      {data.which_won_where_table && data.which_won_where_table.length > 0 && (
        <div className="overflow-x-auto" data-testid="gge-wwwhere-table">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Which-Won-Where Summary Table</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#4F46E5] text-white">
              <th className="px-3 py-2 text-left">Environment</th>
              <th className="px-3 py-2 text-center">Winning Genotype</th>
              <th className="px-3 py-2 text-center">Winning Mean Yield</th>
              <th className="px-3 py-2 text-center">Env Mean</th>
              <th className="px-3 py-2 text-center">Margin Over Env Mean</th>
            </tr></thead>
            <tbody>
              {data.which_won_where_table.map((r, i) => (
                <tr key={r.Environment} className={i % 2 === 0 ? 'bg-white' : 'bg-[#EFEEFB]'}>
                  <td className="px-3 py-1.5 font-medium">{r.Environment}</td>
                  <td className="px-3 py-1.5 text-center font-bold text-[#4F46E5]">{r.Winning_Genotype}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.Winning_Mean_Yield}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.Env_Mean}</td>
                  <td className="px-3 py-1.5 text-center font-mono">+{r.Margin_Over_Env_Mean}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#6E6B66] mt-2 italic">Winner = genotype with the highest mean yield in that environment (raw cell-mean basis). For the visual sector assignment, see the polygon plot above.</p>
        </div>
      )}

      {data.mean_vs_stability_plot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="gge-mean-vs-stability">
          <div className="bg-[#4F46E5]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#4F46E5] uppercase tracking-wider">GGE Biplot 2 · Mean vs Stability (Average Tester Coordinate)</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">AEC abscissa (arrow) measures mean performance; perpendicular axis measures stability. Project each genotype onto the AEC to read its yield rank.</p>
          </div>
          <img src={`data:image/png;base64,${data.mean_vs_stability_plot}`} alt="GGE Mean vs Stability" className="w-full" />
        </div>
      )}

      {data.discrim_vs_repres_plot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="gge-discrim-vs-repres">
          <div className="bg-[#4F46E5]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#4F46E5] uppercase tracking-wider">GGE Biplot 3 · Discriminativeness vs Representativeness</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">Long environment vectors → high discriminating power. Small angles to AEC → representative of the target environment. Ideal test envs have BOTH.</p>
          </div>
          <img src={`data:image/png;base64,${data.discrim_vs_repres_plot}`} alt="GGE Discrim vs Repres" className="w-full" />
        </div>
      )}

      {data.ranking_vs_ideal_plot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="gge-ranking-vs-ideal">
          <div className="bg-[#4F46E5]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#4F46E5] uppercase tracking-wider">GGE Biplot 4 · Ranking vs Ideal Genotype</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">★ = Ideal Genotype (highest mean × zero stability deviation). Genotypes closer to ★ rank higher. Concentric circles indicate equal distance.</p>
          </div>
          <img src={`data:image/png;base64,${data.ranking_vs_ideal_plot}`} alt="GGE Ranking vs Ideal" className="w-full" />
        </div>
      )}

      {/* Genotype scores table */}
      {data.genotype_scores && data.genotype_scores.length > 0 && (
        <div className="overflow-x-auto" data-testid="gge-genotype-scores">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Genotype Scores</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#4F46E5] text-white">
              <th className="px-3 py-2 text-left">Genotype</th>
              <th className="px-3 py-2 text-center">Mean Yield</th>
              <th className="px-3 py-2 text-center">PC1</th>
              <th className="px-3 py-2 text-center">PC2</th>
              <th className="px-3 py-2 text-center">AEC Distance</th>
              <th className="px-3 py-2 text-center text-[10px] opacity-80">|PC2| (legacy)</th>
            </tr></thead>
            <tbody>
              {data.genotype_scores.map((g, i) => (
                <tr key={g.genotype} className={i % 2 === 0 ? 'bg-white' : 'bg-[#EFEEFB]'}>
                  <td className="px-3 py-1.5 font-medium">{g.genotype}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{g.mean_yield}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{g.pc1}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{g.pc2}</td>
                  <td className="px-3 py-1.5 text-center font-mono font-bold text-[#4F46E5]">{g.aec_distance ?? '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono text-[#8A8580]">{g.stability}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.stability_column_caveat && (
            <p className="text-[11px] text-[#B45309] mt-2 italic" data-testid="gge-stability-caveat">{data.stability_column_caveat}</p>
          )}
        </div>
      )}
      {data.environment_scores && data.environment_scores.length > 0 && (
        <div className="overflow-x-auto" data-testid="gge-environment-scores">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Environment Scores</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#E74C3C] text-white">
              <th className="px-3 py-2 text-left">Environment</th>
              <th className="px-3 py-2 text-center">Mean Yield</th>
              <th className="px-3 py-2 text-center">PC1</th>
              <th className="px-3 py-2 text-center">PC2</th>
            </tr></thead>
            <tbody>
              {data.environment_scores.map((e, i) => (
                <tr key={e.environment} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FBEDEB]'}>
                  <td className="px-3 py-1.5 font-medium">{e.environment}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{e.mean_yield}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{e.pc1}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{e.pc2}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   LINE x TESTER VIEW
============================================================ */


export function METView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="met-view">
      {data.interpretation && (
        <div className="bg-[#E8F1FA] rounded-2xl p-5 border border-[#2980B9]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#F5F9F5] rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-[#2C5F2D]">{data.n_genotypes || 0}</div>
          <div className="text-[10px] text-[#8A8580] uppercase">Genotypes</div>
        </div>
        <div className="bg-[#E8F1FA] rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-[#2980B9]">{data.n_environments || 0}</div>
          <div className="text-[10px] text-[#8A8580] uppercase">Environments</div>
        </div>
        <div className="bg-[#F3E8FF] rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-[#9B59B6]">{data.n_reps || 0}</div>
          <div className="text-[10px] text-[#8A8580] uppercase">Reps</div>
        </div>
      </div>

      {/* Combined ANOVA per trait */}
      {data.combined_anova && Object.entries(data.combined_anova).map(([trait, anova]) => (
        <div key={trait} className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">{trait} - Combined ANOVA</h4>
          <table className="w-full text-xs border-collapse" data-testid={`met-anova-${trait}`}>
            <thead><tr className="bg-[#2980B9] text-white">
              {['Source', 'DF', 'SS', 'MS', 'F', 'P', 'Sig'].map(h => (
                <th key={h} className="px-3 py-2 text-center font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(anova).map(([src, d], i) => (
                <tr key={src} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F6FC]'}>
                  <td className="px-3 py-2 font-medium text-left">{src}</td>
                  <td className="px-3 py-2 text-center font-mono">{d?.DF ?? '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d?.SS === 'number' ? d.SS.toFixed(2) : '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d?.MS === 'number' ? d.MS.toFixed(2) : '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d?.F === 'number' ? d.F.toFixed(2) : '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d?.P === 'number' ? d.P.toFixed(4) : '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {d?.Sig && <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${d.Sig === '**' ? 'bg-emerald-500 text-white' : d.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{d.Sig}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Heritability & Genetic Gain */}
      {data.heritability && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Heritability & Genetic Gain</h4>
          <table className="w-full text-xs border-collapse" data-testid="met-heritability">
            <thead><tr className="bg-[#2980B9] text-white">
              <th className="px-3 py-2 text-left">Trait</th>
              <th className="px-3 py-2 text-center">H<sup>2</sup></th>
              <th className="px-3 py-2 text-center">H<sup>2</sup> %</th>
              <th className="px-3 py-2 text-center">Genetic Gain</th>
              <th className="px-3 py-2 text-center">Gain %</th>
              <th className="px-3 py-2 text-center">Grand Mean</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.heritability).map(([trait, h], i) => {
                const gg = data.genetic_gain?.[trait] || {};
                return (
                  <tr key={trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F6FC]'}>
                    <td className="px-3 py-2 font-medium">{trait}</td>
                    <td className="px-3 py-2 text-center font-bold text-[#2C5F2D]">{h.H2_broad_sense}</td>
                    <td className="px-3 py-2 text-center">{h.H2_percentage}%</td>
                    <td className="px-3 py-2 text-center">{gg.expected_genetic_gain ?? '-'}</td>
                    <td className="px-3 py-2 text-center font-semibold text-[#E67E22]">{gg.genetic_gain_percent ?? '-'}%</td>
                    <td className="px-3 py-2 text-center">{gg.grand_mean ?? '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* G×E Interaction */}
      {data.gxe_interaction && Object.entries(data.gxe_interaction).map(([trait, gxe]) => (
        <div key={trait} className="p-3 bg-[#F0F6FC] rounded-xl border border-[#2980B9]/15">
          <div className="flex items-center gap-3 mb-1">
            <span className="font-semibold text-sm text-[#2C2A28]">{trait} G×E</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold
              ${gxe.significance === '**' ? 'bg-red-100 text-red-700' : gxe.significance === '*' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
              {gxe.significance} (F={gxe.f_value}, P={gxe.p_value})
            </span>
          </div>
          <p className="text-xs text-[#5E5A56]">{gxe.conclusion}</p>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   HERITABILITY & GENETIC ADVANCE VIEW
============================================================ */
